import { nextTick } from 'vue';

/**
 * Will resize the labels in a form to the largest label size + 20% for padding
 * @param nodeList
 * @param maxWidth
 * @returns
 */
const resizeLabel = (nodeList: NodeListOf<Element>, maxWidth: number = 120) => {
    if (nodeList.length === 0) {
        return;
    }

    // Remove the elements that does not contain a label
    const filtered = Array.from(nodeList).filter((el) => el.querySelector('label'));

    filtered.forEach((el) => {
        // One filtered row can have multiple labels. We need to find the largest label
        const labels = el.querySelectorAll('label');
        let maxWidth = 0;
        labels.forEach((label) => {
            maxWidth = Math.max(maxWidth, (label as HTMLElement).clientWidth);
        });
    });

    if (maxWidth > 300) {
        maxWidth = 300;
    }

    // set the new width for all labels to the largest label size
    filtered.forEach((el) => {
        // One filtered row can have multiple labels. We need to find the largest label
        const labels = el.querySelectorAll('label');
        labels.forEach((label) => {
            (label as HTMLElement).style.minWidth = maxWidth + 'px';
            (label as HTMLElement).classList.remove('text-nowrap'); // We remove the nowrap class to allow the label to grow if we change language and the text is longer
        });
    });
};

const setElementFocus = (elemtnid: string) => {
    // Should maybe use nextTick but it doesnt work. So we use setTimeout instead
    // Need to wait for the view to be rendered before we can set focus. There should be an event for this somewhere. but I cant find it.
    setTimeout(() => {
        const element = document.getElementById(elemtnid);
        if (!element) return;
        nextTick(() => element.focus());
    }, 100);
};

/**
 * Will set focus to the first input or button in the view or modal
 * @param selectInvalid if true, we will select the first invalid input
 * @returns
 */
const setFocus = (selectInvalid: boolean = false, el: HTMLElement | null = null) => {
    // Should maybe use nextTick but it doesnt work. So we use setTimeout instead
    // Need to wait for the view to be rendered before we can set focus. There should be an event for this somewhere. but I cant find it.
    setTimeout(() => {
        // Check if there is a element with autofocus attribute or else select the first input or button
        const elements = document.querySelectorAll('.modal.show');
        let element: HTMLElement | null = null;
        if (elements.length > 0) {
            element = elements[elements.length - 1] as HTMLElement;
        }

        if (!element) {
            element = document.querySelector('.eq-viewcontent');
        }
        if (!element) return;

        const allElements = element.querySelectorAll('input');

        let focusElement: HTMLElement | null = el;

        if (focusElement == null) {
            allElements.forEach((el) => {
                // Skip all that is not visible
                if (!el.checkVisibility()) return;

                // if we find an element with autofocus we set focus to that element
                if (!selectInvalid && el.autofocus) {
                    focusElement = el;
                    return;
                }

                // if we dont have a focus element we set focus to the first input
                if (!focusElement) {
                    if (!selectInvalid || (selectInvalid && !el.validity.valid)) {
                        focusElement = el;
                    }
                }
            });
        }

        // if we still dont have a focus element we check for a button
        if (!focusElement) {
            const buttons = element.querySelectorAll('.btn') as NodeListOf<HTMLButtonElement>;
            buttons.forEach((el: HTMLButtonElement) => {
                if (el.autofocus) {
                    focusElement = el;
                    return;
                }
                if (!focusElement && el.checkVisibility()) {
                    focusElement = el as HTMLButtonElement;
                }
            });
        }
        if (!focusElement) return;

        nextTick(() => focusElement!.focus());
    }, 100);
};

/**
 * Helper method to stop propagation of an event
 * @param event
 */
const stopPropagation = (event?: Event): boolean => {
    event?.stopPropagation();
    event?.preventDefault();
    event?.stopImmediatePropagation();

    return false;
};

/**
 * Helper class for simple helper functions
 * @returns
 */
export const useHelper = () => {
    return { resizeLabel, setFocus, stopPropagation, setElementFocus };
};
